// @djsem/shared — Release Campaign Planner (36), Post-Show Recorder (37),
// Library Cleanup (38), Pro SDK (39), Marketplace (35).
//
// Five modules in one file, each clearly delimited. All follow established
// contract + service patterns. No UI or backend routes — those are dependency-reported.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "../index";
import { SeqCounter } from "../runtime";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 37 — Post-Show Recorder / Recap Generator
// ============================================================================

export type DJSEMHighlightType = "drop" | "vocal_moment" | "manual_cue" | "lighting_peak" | "crowd_peak_manual" | "transition" | "custom";
export type DJSEMHighlightSuggestedUse = "instagram_reel" | "tiktok" | "youtube_short" | "aftermovie" | "client_preview" | "archive";

export interface DJSEMPerformedTrackSummary {
  trackId: string;
  title?: string; artist?: string;
  startedAt: string; endedAt?: string;
  peakEnergy?: number;
  notableCueIds: string[];
  warnings: string[];
}

export interface DJSEMPerformanceSessionSummary {
  id: string;
  projectId?: string; setId?: string;
  startedAt: string; endedAt?: string;
  trackSummaries: DJSEMPerformedTrackSummary[];
  cueEvents: string[];
  highlightIds: string[];
  warnings: string[];
}

export interface DJSEMPostShowHighlight {
  id: string;
  sessionId: string;
  startTimeMs: number;
  endTimeMs: number;
  highlightType: DJSEMHighlightType;
  confidence: number;
  sourceEventIds: string[];
  suggestedUse: DJSEMHighlightSuggestedUse;
  warnings: string[];
}

export interface DJSEMAftermovieDraft {
  id: string;
  sessionId: string;
  title: string;
  highlightIds: string[];
  assetIds: string[];
  renderJobIds: string[];
  status: "draft" | "ready_to_render" | "rendering" | "completed" | "failed";
  warnings: string[];
}

export interface SummarizeSessionInput {
  sessionId: string; projectId?: string; setId?: string;
  startedAt: string; endedAt?: string;
  performedTracks: DJSEMPerformedTrackSummary[];
  cueEventIds: string[];
}

export interface DetectHighlightsInput {
  sessionId: string;
  events: Array<{ id: string; type: DJSEMHighlightType; timeMs: number; durationMs: number; confidence: number; }>;
}

export class PostShowSessionService {
  summarizeSession(input: SummarizeSessionInput): DJSEMPerformanceSessionSummary {
    return {
      id: input.sessionId, projectId: input.projectId, setId: input.setId,
      startedAt: input.startedAt, endedAt: input.endedAt,
      trackSummaries: input.performedTracks,
      cueEvents: input.cueEventIds, highlightIds: [],
      warnings: [],
    };
  }

  detectHighlights(input: DetectHighlightsInput): DJSEMPostShowHighlight[] {
    // Only use deterministic sources — never infer "crowd peak" without explicit data
    return input.events
      .filter(e => e.confidence >= 0.5 && e.type !== "crowd_peak_manual") // crowd_peak_manual requires explicit marking
      .map(e => ({
        id: `hl_${_seq.next()}`, sessionId: input.sessionId,
        startTimeMs: e.timeMs, endTimeMs: e.timeMs + e.durationMs,
        highlightType: e.type, confidence: e.confidence,
        sourceEventIds: [e.id],
        suggestedUse: (e.type === "drop" ? "instagram_reel" : "archive") as DJSEMHighlightSuggestedUse,
        warnings: [],
      }));
  }
}

export class AftermovieDraftService {
  createDraft(sessionId: string, title: string): DJSEMAftermovieDraft {
    return { id: `amd_${_seq.next()}`, sessionId, title, highlightIds: [], assetIds: [], renderJobIds: [], status: "draft", warnings: [] };
  }

  addHighlight(draft: DJSEMAftermovieDraft, highlightId: string): DJSEMAftermovieDraft {
    return { ...draft, highlightIds: [...draft.highlightIds, highlightId] };
  }

  validateDraft(draft: DJSEMAftermovieDraft): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (draft.highlightIds.length === 0) issues.push({ path: "$.highlightIds", message: "no highlights selected" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "aftermovie_draft");
  }
}
